<template>
  <div>
    <div
      v-if="isOverlayVisible"
      class="overlay"
      @click="handleOverlayClick"
    ></div>
    <div
      v-if="hasSecondaryMenu"
      :class="[
        'h-full overflow-auto w-[13rem] flex flex-col bg-white dark:bg-slate-900 border-r dark:border-slate-800/50 rtl:border-r-0 rtl:border-l border-slate-50 text-sm px-2 pb-8',
        hoverClassActive ? 'hover-class' : '',
        displayNoneActive ? 'display-none' : '',
      ]"
      @mouseleave="handleMouseLeave"
    >
      <!-- <h5 class="heading-text">Conversations</h5> -->
      <h5 class="dark:text-slate-200 heading-text">{{ capitalizedParentNav }}</h5>
      <hr class="hr-style"/>
      <account-context @toggle-accounts="toggleAccountModal" />
      <transition-group
        name="menu-list"
        tag="ul"
        class="pt-2 list-none ml-0 mb-0"
      >
        <secondary-nav-item
          v-for="menuItem in accessibleMenuItems"
          :key="menuItem.toState"
          :menu-item="menuItem"
          @click.native="handleAdditionalNavItemClick"
        />
        <secondary-nav-item
          v-for="menuItem in additionalSecondaryMenuItems[menuConfig.parentNav]"
          :key="menuItem.key"
          :menu-item="menuItem"
          @add-label="showAddLabelPopup"
          @add-board="showAddBoardPopup"
          @click.native="handleAdditionalNavItemClick"
        />
      </transition-group>
    </div>
  </div>
</template>
<script>
import { frontendURL } from '../../../helper/URLHelper';
import SecondaryNavItem from './SecondaryNavItem.vue';
import AccountContext from './AccountContext.vue';
import { mapGetters } from 'vuex';
import { FEATURE_FLAGS } from '../../../featureFlags';
import adminMixin from '../../../mixins/isAdmin';
import { dailyWorkSettingAPI } from '../../../api/dailyWork';
import {
  findDailyTaskView,
  isDailyTaskView,
} from '../../../helper/dailyTaskCustomViews';

export default {
  components: {
    AccountContext,
    SecondaryNavItem,
  },
  mixins: [adminMixin],
  props: {
    accountId: {
      type: Number,
      default: 0,
    },
    labels: {
      type: Array,
      default: () => [],
    },
    inboxes: {
      type: Array,
      default: () => [],
    },
    teams: {
      type: Array,
      default: () => [],
    },
    customViews: {
      type: Array,
      default: () => [],
    },
    menuConfig: {
      type: Object,
      default: () => {},
    },
    currentRole: {
      type: String,
      default: '',
    },
    isOnChatwootCloud: {
      type: Boolean,
      default: false,
    },
    boards:{
      type:Array,
      default: () => [],
    }
  },
  data() {
    return {
      hoverClassActive: false, // To control hover-class
      displayNoneActive: true,
      isOverlayVisible: false,
      dailyTaskAgents: [],
    };
  },
  mounted() {
    // Using EventBus
    bus.$on('toggle-secondary-menu', this.toggleSecondaryMenu);
    this.loadDailyTaskSidebarAgents();
  },
  watch: {
    accountId() {
      this.loadDailyTaskSidebarAgents();
    },
    isAdmin() {
      this.loadDailyTaskSidebarAgents();
    },
  },
  computed: {
    ...mapGetters({
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      agents: 'agents/getAgents',
      currentUserID: 'getCurrentUserID',
    }),
    hasSecondaryMenu() {
      return this.menuConfig.menuItems && this.menuConfig.menuItems.length;
    },
    accessibleMenuItems() {
      if (!this.currentRole) {
        return [];
      }
      const menuItemsFilteredByRole = this.menuConfig.menuItems.filter(
        menuItem =>
          window.roleWiseRoutes[this.currentRole].indexOf(
            menuItem.toStateName
          ) > -1
      );
      return menuItemsFilteredByRole.filter(item => {
        if (item.showOnlyOnCloud) {
          return this.isOnChatwootCloud;
        }
        if (item.featureFlag) {
          return this.isFeatureEnabledonAccount(this.accountId, item.featureFlag);
        }
        return true;
      });
    },
    inboxSection() {
      return {
        icon: 'folder',
        label: 'INBOXES',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.INBOX_MANAGEMENT),
        newLinkTag: 'NEW_INBOX',
        key: 'inbox',
        toState: frontendURL(`accounts/${this.accountId}/settings/inboxes/new`),
        toStateName: 'settings_inbox_new',
        newLinkRouteName: 'settings_inbox_new',
        children: this.inboxes
          .map(inbox => ({
            id: inbox.id,
            label: inbox.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/inbox/${inbox.id}`
            ),
            type: inbox.channel_type,
            phoneNumber: inbox.phone_number,
            reauthorizationRequired: inbox.reauthorization_required,
          }))
          .sort((a, b) =>
            a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
          ),
      };
    },
    labelSection() {
      return {
        icon: 'number-symbol',
        label: 'LABELS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_LABEL',
        key: 'label',
        toState: frontendURL(`accounts/${this.accountId}/settings/labels`),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddLabel',
        children: this.labels.map(label => ({
          id: label.id,
          label: label.title,
          color: label.color,
          truncateLabel: true,
          automation: label.automation,
          contactAutomation: label.contact_automation,
          excludeFromDefaultViews: label.exclude_from_default_views,
          toState: frontendURL(
            `accounts/${this.accountId}/label/${label.title}`
          ),
        })),
      };
    },
    contactLabelSection() {
      return {
        icon: 'number-symbol',
        label: 'TAGGED_WITH',
        hasSubMenu: true,
        key: 'label',
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_LABEL',
        toState: frontendURL(`accounts/${this.accountId}/settings/labels`),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddLabel',
        children: this.labels.map(label => ({
          id: label.id,
          label: label.title,
          color: label.color,
          truncateLabel: true,
          automation: label.automation,
          contactAutomation: label.contact_automation,
          excludeFromDefaultViews: label.exclude_from_default_views,
          toState: frontendURL(
            `accounts/${this.accountId}/labels/${label.title}/contacts`
          ),
        })),
      };
    },
    teamSection() {
      return {
        icon: 'people-team',
        label: 'TEAMS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_TEAM',
        key: 'team',
        toState: frontendURL(`accounts/${this.accountId}/settings/teams/new`),
        toStateName: 'settings_teams_new',
        newLinkRouteName: 'settings_teams_new',
        children: this.teams.map(team => ({
          id: team.id,
          label: team.name,
          truncateLabel: true,
          toState: frontendURL(`accounts/${this.accountId}/team/${team.id}`),
        })),
      };
    },
    foldersSection() {
      return {
        icon: 'folder',
        label: 'CUSTOM_VIEWS_FOLDER',
        hasSubMenu: true,
        key: 'custom_view',
        children: this.customViews
          .filter(view => view.filter_type === 'conversation')
          .map(view => ({
            id: view.id,
            label: view.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/custom_view/${view.id}`
            ),
          })),
      };
    },
    contactSegmentItems() {
      return this.customViews
        .filter(view => view.filter_type === 'contact' && !isDailyTaskView(view))
        .map(view => ({
          id: view.id,
          label: view.name,
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${this.accountId}/contacts/custom_view/${view.id}`
          ),
        }));
    },
    contactSegmentsSection() {
      return {
        icon: 'folder',
        label: 'CUSTOM_VIEWS_SEGMENTS',
        hasSubMenu: true,
        key: 'custom_view',
        children: this.contactSegmentItems,
      };
    },
    contactDailyTaskViews() {
      return this.customViews.filter(
        view => view.filter_type === 'contact' && isDailyTaskView(view)
      );
    },
    contactTasksSection() {
      const ownView = findDailyTaskView(
        this.contactDailyTaskViews,
        this.currentUserID
      );
      const myTaskItem = {
        id: ownView ? `daily_task_view_${ownView.id}` : 'my_task',
        label: this.$t('SIDEBAR.MY_TASK'),
        truncateLabel: true,
        toState: this.dailyTaskViewPath(ownView, this.currentUserID),
      };
      return {
        icon: 'folder',
        label: 'TASKS',
        hasSubMenu: true,
        key: 'daily_tasks',
        children: [myTaskItem, ...this.dailyTaskAgentMenuItems],
      };
    },
    dailyTaskAgentMenuItems() {
      if (!this.isAdmin) return [];
      const currentId = Number(this.currentUserID);
      return this.dailyTaskAgents
        .filter(agent => agent.id != null && Number(agent.id) !== currentId)
        .map(agent => {
          const view = findDailyTaskView(this.contactDailyTaskViews, agent.id);
          return {
            id: view ? `daily_task_view_${view.id}` : `daily_task_agent_${agent.id}`,
            label: this.$t('SIDEBAR.AGENT_TASK', { name: agent.name }),
            truncateLabel: true,
            toState: this.dailyTaskViewPath(view, agent.id),
          };
        });
    },
    boardSection() {
      return {
        icon: 'number-symbol',
        label: 'BOARDS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_BOARD',
        key: 'board',
        toState: frontendURL(`accounts/${this.accountId}/settings/boards`),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddBoard',
        children: this.boards.map(board => ({
          id: board.id,
          label: board.name,
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${this.accountId}/pipelines/boards/${board.id}`
          ),
        })),
      };
    },
    additionalSecondaryMenuItems() {
      let conversationMenuItems = [this.inboxSection, this.labelSection];
      let contactMenuItems = [this.contactTasksSection, this.contactLabelSection];
      let boardMenuItems = [this.boardSection];
      if (this.teams.length) {
        conversationMenuItems = [this.teamSection, ...conversationMenuItems];
      }
      if (this.customViews.length) {
        conversationMenuItems = [this.foldersSection, ...conversationMenuItems];
      }
      if (this.contactSegmentItems.length) {
        contactMenuItems = [
          this.contactTasksSection,
          this.contactSegmentsSection,
          this.contactLabelSection,
        ];
      }
      return {
        conversations: conversationMenuItems,
        contacts: contactMenuItems,
        board: boardMenuItems,
      };
    },
    capitalizedParentNav() {
    if (this.menuConfig.parentNav) {
      return this.menuConfig.parentNav.charAt(0).toUpperCase() + this.menuConfig.parentNav.slice(1);
    }
    return '';
  },
  },
  methods: {
    dailyTaskViewPath(view, userId) {
      if (view?.id) {
        return frontendURL(
          `accounts/${this.accountId}/contacts/custom_view/${view.id}`
        );
      }
      if (userId && Number(userId) !== Number(this.currentUserID)) {
        return frontendURL(
          `accounts/${this.accountId}/contacts/daily-tasks`,
          { user_id: userId }
        );
      }
      return frontendURL(`accounts/${this.accountId}/contacts/daily-tasks`);
    },
    async loadDailyTaskSidebarAgents() {
      if (this.menuConfig.parentNav === 'contacts' && this.accountId) {
        this.$store.dispatch('customViews/get', 'contact');
      }
      if (!this.isAdmin || !this.accountId) {
        this.dailyTaskAgents = [];
        return;
      }
      try {
        this.$store.dispatch('agents/get');
        const { data } = await dailyWorkSettingAPI.get();
        this.dailyTaskAgents = (data.agent_settings || [])
          .map(row => {
            const id = row.user_id;
            if (id == null || id === '') return null;
            const agent = this.agents.find(
              user => Number(user.id) === Number(id)
            );
            return {
              id: Number(id),
              name:
                row.name ||
                agent?.name ||
                agent?.user?.name ||
                `User #${id}`,
            };
          })
          .filter(Boolean);
      } catch (_) {
        this.dailyTaskAgents = [];
      }
    },
    toggleSecondaryMenu(isVisible) {
      this.hoverClassActive = isVisible;
      this.displayNoneActive = !isVisible;
      this.isOverlayVisible = isVisible;
    },
    showAddLabelPopup() {
      this.$emit('add-label');
    },
    showAddBoardPopup(){
      this.$emit('add-board')
    },
    toggleAccountModal() {
      this.$emit('toggle-accounts');
    },
    showNewLink(featureFlag) {
      return this.isFeatureEnabledonAccount(this.accountId, featureFlag);
    },
    handleAdditionalNavItemClick() {
      // Toggle class states on additional nav item click
      this.hoverClassActive = false;
      this.displayNoneActive = true;
      this.isOverlayVisible = true;
    },
    handleMouseLeave() {
      this.displayNoneActive = true; // Set display-none to true when mouse leaves
      this.isOverlayVisible = false;
    },
    handleOverlayClick() {
      // Hide secondary menu and overlay when the overlay is clicked
      this.hoverClassActive = false;
      this.displayNoneActive = true;
      this.isOverlayVisible = false;
    },
  },
};
</script>
<style scoped>
.overlay {
  position: fixed; /* Fixed positioning to cover the whole screen */
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5); /* Semi-transparent black */
  /* z-index hierarchy: sidebar overlay should be above content (10) but below modals (50+) */
  z-index: 25; /* Make sure it's above the other content */
}

.hover-class {
  position: absolute;
  z-index: 30;
  left: 55px;
  box-shadow: 4px 4px 10px rgba(0, 0, 0, 0.1);
}
body:not(.dark) .hover-class {
  background-color: aliceblue;
}
.display-none {
  display: none;
}
.heading-text {
  font-weight: 700;
  text-align: center;
  margin: 5px 0px;
}
body:not(.dark) .heading-text{
  color: #40518a;
}
.hr-style {
  margin: 5px !important;
  color: gray;
}
</style>
